/**
 * Bilan chiffré comparé de fin de mandat (ADR 0015) : remplace « réélu / battu ». Les résultats du joueur sont comparés, sur des
 * indicateurs définis dans `content/scorecard.json`, à deux contrefactuels calculés avec la MÊME graine et la MÊME séquence
 * d'événements (celle de la partie du joueur, figée, ADR 0009 §4), décisions de crise = options de référence :
 *   - « si rien ne change » : programme vide, aucun financement ;
 *   - « budget du gouvernement » : le préréglage de référence (`scorecard.reference_preset_id`, PLF 2027).
 * Aucune pondération : le résumé est un décompte neutre (« meilleur que le tendanciel sur X indicateurs sur Y ») ; des poids
 * choisis par le joueur s'appliquent seulement à l'affichage (`weightedSummary`). Pur et déterministe.
 */
import { attribute2032 } from "./attribution.js";
import { referenceOption } from "./politics.js";
import { indicatorStart, indicatorValue, mechanismStatus } from "./scorecard-values.js";
import { simulate } from "./simulate.js";
import type {
  Baseline,
  Choices,
  Financing,
  GapItem,
  ParamSet,
  PresetProgramme,
  Programme,
  ScheduledEvent,
  Scorecard,
  ScorecardComparison,
  ScorecardContent,
  ScorecardCount,
  ScorecardExplanation,
  ScorecardIndicator,
  ScorecardIndicatorDef,
  SimOptions,
  Timeline,
} from "./types.js";

export { indicatorValue, indicatorStart, mechanismStatus, resolveMechanismParams } from "./scorecard-values.js";

export interface ScorecardOptions {
  /** Mode réaliste (défaut : true pour le bilan, ADR 0015 §3). */
  realistic?: boolean;
}

const EMPTY_PROGRAMME: Programme = { measure_ids: [], hyp: {} };
const NO_FINANCING: Financing = { revenue_raises: {}, across_the_board_cut: 0 };

/** Seuil d'égalité d'un indicateur : la moitié du dernier chiffre affiché. */
export function equalityEpsilon(def: Pick<ScorecardIndicatorDef, "decimals">): number {
  return Math.pow(10, -def.decimals) / 2;
}

/** Comparaison selon le sens propre de l'indicateur. */
export function compare(def: Pick<ScorecardIndicatorDef, "decimals" | "better_is">, player: number | null, ref: number | null): ScorecardComparison {
  if (player === null || ref === null) return "na";
  const d = player - ref;
  if (Math.abs(d) < equalityEpsilon(def)) return "equal";
  return (def.better_is === "lower" ? -d : d) > 0 ? "better" : "worse";
}

/** Séquence d'événements de la partie, décisions remplacées par l'option de référence (contrefactuels du bilan). */
export function referenceSchedule(player: Timeline, content: ScorecardContent): ScheduledEvent[] {
  return player.years.flatMap((y, i) =>
    y.events_fired.map((id) => {
      const e = content.events.find((ev) => ev.id === id);
      const ref = e ? referenceOption(e) : undefined;
      const chosen = y.choices_applied?.[id] ?? "";
      const optionId = ref ? ref.id : chosen;
      // Même règle que l'attribution (ADR 0009 §4) : l'issue du risque tirée dans la partie est conservée si la référence a un risque.
      const hit = y.risks_hit?.[id] ?? false;
      return { turn: i + 1, event_id: id, option_id: optionId, risk_hit: ref ? (ref.risk ? hit : false) : hit };
    }),
  );
}

/** Préréglage de référence (erreur de contenu explicite s'il manque). */
export function referencePreset(content: ScorecardContent): PresetProgramme {
  const id = content.scorecard.reference_preset_id;
  const p = content.presets.find((x) => x.id === id);
  if (!p) throw new Error(`scorecard : préréglage de référence « ${id} » absent de presets`);
  return p;
}

const presetProgramme = (p: PresetProgramme): Programme => ({ measure_ids: [...p.measure_ids], hyp: {} });

function baselineFigure(baseline: Baseline, path: string | undefined): number | null {
  if (!path) return null;
  const [block, key] = path.split(".");
  if (!block || !key) return null;
  const rec = (baseline as unknown as Record<string, Record<string, { value?: number | null } | undefined> | undefined>)[block];
  const v = rec?.[key]?.value;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function count(xs: ScorecardComparison[]): ScorecardCount {
  const c: ScorecardCount = { better: 0, worse: 0, equal: 0, total: 0 };
  for (const x of xs) {
    if (x === "na") continue;
    c[x]++;
    c.total++;
  }
  return c;
}

interface Runs {
  player: Timeline;
  trend: Timeline;
  plf: Timeline;
  preset: PresetProgramme;
  schedule: ScheduledEvent[];
  year: number;
  opts: SimOptions;
}

function runAll(baseline: Baseline, params: ParamSet, programme: Programme, financing: Financing, choices: Choices, seed: number, content: ScorecardContent, options: ScorecardOptions): Runs {
  const realistic = options.realistic ?? true;
  const opts: SimOptions = realistic ? { realistic: true } : {};
  const player = simulate(baseline, params, programme, financing, choices, seed, content, opts);
  const schedule = referenceSchedule(player, content);
  const preset = referencePreset(content);
  const trend = simulate(baseline, params, EMPTY_PROGRAMME, NO_FINANCING, {}, seed, content, { ...opts, schedule });
  const plf = simulate(baseline, params, presetProgramme(preset), preset.financing, {}, seed, content, { ...opts, schedule });
  const last = player.years[player.years.length - 1];
  return { player, trend, plf, preset, schedule, year: last ? last.year : player.base_year, opts };
}

/**
 * Bilan chiffré comparé. Mêmes arguments que `simulate` ; `content` porte en plus `scorecard` (définitions) et `presets`.
 * L'ordre des indicateurs de `content.scorecard.indicators` ne change aucune valeur ni aucun décompte (test de neutralité).
 */
export function scorecard(
  baseline: Baseline,
  params: ParamSet,
  programme: Programme,
  financing: Financing,
  choices: Choices,
  seed: number,
  content: ScorecardContent,
  options: ScorecardOptions = {},
): Scorecard {
  const r = runAll(baseline, params, programme, financing, choices, seed, content, options);
  const indicators: ScorecardIndicator[] = content.scorecard.indicators.map((d) => {
    const player = indicatorValue(d, r.player, r.year);
    const trend = indicatorValue(d, r.trend, r.year);
    const plf = indicatorValue(d, r.plf, r.year);
    const mech = mechanismStatus(params, d.mechanism_params);
    const unsourcedOut = d.counted !== false && d.count_requires_sourced === true && mech.status !== "mecanique_sourcee";
    const counted = d.counted !== false && !unsourcedOut;
    return {
      id: d.id,
      label: d.label,
      unit: d.unit,
      ...(d.group !== undefined ? { group: d.group } : {}),
      definition: d.definition,
      decimals: d.decimals,
      better_is: d.better_is,
      counted,
      ...(counted ? {} : { uncounted_reason: unsourcedOut ? "mécanique non sourcée" : (d.uncounted_reason ?? "même grandeur") }),
      start: indicatorStart(d, r.player, baselineFigure(baseline, d.start_figure)),
      player,
      trend,
      plf,
      delta_vs_trend: player !== null && trend !== null ? player - trend : null,
      delta_vs_plf: player !== null && plf !== null ? player - plf : null,
      vs_trend: compare(d, player, trend),
      vs_plf: compare(d, player, plf),
      source_status: mech.status,
      mechanism: { total: mech.total, sourced: mech.sourced, two_sources: mech.two_sources, unsourced: mech.unsourced },
    };
  });
  const counted = indicators.filter((i) => i.counted);
  const sourced = counted.filter((i) => i.source_status !== "mecanique_non_sourcee");
  const by_group: Scorecard["summary"]["by_group"] = {};
  for (const g of [...new Set(counted.map((i) => i.group ?? ""))].sort()) {
    const xs = counted.filter((i) => (i.group ?? "") === g);
    by_group[g] = { vs_trend: count(xs.map((i) => i.vs_trend)), vs_plf: count(xs.map((i) => i.vs_plf)) };
  }
  return {
    year: r.year,
    start_year: r.player.start?.["year"] ?? r.player.base_year,
    realistic: options.realistic ?? true,
    reference: { id: r.preset.id, label: r.preset.label },
    indicators,
    player: r.player,
    counterfactuals: { trend: r.trend, plf: r.plf },
    interrupted: { player: r.player.game_over, trend: r.trend.game_over, plf: r.plf.game_over },
    summary: {
      vs_trend: count(counted.map((i) => i.vs_trend)),
      vs_plf: count(counted.map((i) => i.vs_plf)),
      sourced_vs_trend: count(sourced.map((i) => i.vs_trend)),
      sourced_vs_plf: count(sourced.map((i) => i.vs_plf)),
      by_group,
    },
  };
}

/**
 * Résumé pondéré par les priorités DU JOUEUR (jamais par défaut dans le moteur) : part du poids total du côté « mieux » / « moins
 * bien » / « égal ». Poids absent = `defaultWeight`. Comparaison au tendanciel (`vs_trend`) ou au gouvernement (`vs_plf`).
 */
export function weightedSummary(
  indicators: Pick<ScorecardIndicator, "id" | "vs_trend" | "vs_plf" | "counted">[],
  weights: Record<string, number>,
  defaultWeight: number,
  against: "vs_trend" | "vs_plf",
): { better: number; worse: number; equal: number; total: number } {
  const out = { better: 0, worse: 0, equal: 0, total: 0 };
  for (const i of indicators) {
    const c = i[against];
    if (c === "na" || !i.counted) continue;
    const w = weights[i.id] ?? defaultWeight;
    if (!(w > 0)) continue;
    out[c] += w;
    out.total += w;
  }
  return out;
}

/* ------------------------------------------------------------------ « Pourquoi ? » par indicateur */

const fmt = (x: number, dec: number): string => x.toFixed(dec).replace(".", ",").replace("-", "−");
const signed = (x: number, dec: number): string => (x > 0 ? "+" : x < 0 ? "−" : "") + Math.abs(x).toFixed(dec).replace(".", ",");

/** Gabarit fermé : écart, deux premiers facteurs, interactions. Aucun texte libre. */
export function gapRationale(def: ScorecardIndicatorDef, year: number, player: number | null, ref: number | null, refLabel: string, items: GapItem[], residual: number | null): string {
  if (player === null || ref === null) return `${def.label} : comparaison indisponible (mandat interrompu avant ${year}).`;
  const eps = equalityEpsilon(def);
  const u = def.unit;
  const head = `Fin ${year}, ${def.label.toLowerCase()} : ${fmt(player, def.decimals)} ${u} contre ${fmt(ref, def.decimals)} ${refLabel}`;
  const gap = player - ref;
  if (Math.abs(gap) < eps && items.every((i) => Math.abs(i.delta) < eps)) return `${head} : même résultat.`;
  const top = items.filter((i) => Math.abs(i.delta) >= eps).slice(0, 2);
  const parts = top.map((i) => `${i.kind === "reference" ? "l'absence dans ton programme de " : ""}« ${i.label} » (${signed(i.delta, def.decimals)})`);
  const why = parts.length ? ` Principaux facteurs : ${parts.join(", puis ")}.` : " Aucun élément ne pèse seul plus que la précision affichée.";
  const inter = residual !== null && Math.abs(residual) >= eps ? ` Interactions entre éléments : ${signed(residual, def.decimals)}.` : "";
  return `${head} (écart ${signed(gap, def.decimals)}).${why}${inter}`;
}

/**
 * Explication de l'écart de chaque indicateur (ADR 0015 §4) :
 * - contre le tendanciel : contributions marginales de l'attribution (`attribute2032`, mode et indicateurs du bilan) — mesures,
 *   financement, décisions de crise (option retenue vs référence) ; les chocs subis sont communs au joueur et au tendanciel et
 *   n'entrent pas dans l'écart ;
 * - contre le budget du gouvernement : mêmes contributions, moins celles du préréglage contre le tendanciel (un élément présent
 *   des deux côtés se compense) ; le reste du préréglage apparaît en `kind: "reference"`.
 * Résidu = écart − Σ contributions (interactions, seuils).
 */
export function explainScorecard(
  baseline: Baseline,
  params: ParamSet,
  programme: Programme,
  financing: Financing,
  choices: Choices,
  seed: number,
  content: ScorecardContent,
  options: ScorecardOptions = {},
): ScorecardExplanation {
  const r = runAll(baseline, params, programme, financing, choices, seed, content, options);
  const defs = content.scorecard.indicators;
  const realistic = options.realistic ?? true;
  const empty: ScorecardExplanation = { year: r.year, indicators: defs.map((d) => ({ id: d.id, vs_trend: { gap: null, items: [], residual: null, rationale: gapRationale(d, r.year, null, null, "", [], null) }, vs_plf: { gap: null, items: [], residual: null, rationale: gapRationale(d, r.year, null, null, "", [], null) } })) };
  if (r.player.years.length === 0) return empty;
  const base = { realistic, indicators: defs, skipShocks: true };
  const mine = attribute2032(baseline, params, programme, financing, choices, seed, content, base).items.filter((i) => i.kind !== "shock" && !(i.kind === "event" && i.option_id === i.reference_option_id));
  const theirs = attribute2032(baseline, params, presetProgramme(r.preset), r.preset.financing, {}, seed, content, { ...base, schedule: r.schedule }).items.filter((i) => i.kind === "measure" || i.kind === "financing");
  const indicators = defs.map((d) => {
    const player = indicatorValue(d, r.player, r.year);
    const trend = indicatorValue(d, r.trend, r.year);
    const plf = indicatorValue(d, r.plf, r.year);
    const order = (xs: GapItem[]): GapItem[] => [...xs].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || a.id.localeCompare(b.id));
    const itemsTrend = order(mine.map((i) => ({ kind: i.kind as GapItem["kind"], id: i.id, label: i.label, delta: i.indicators?.[d.id] ?? 0 })));
    // Contre le gouvernement : Σ (joueur − tendanciel) − Σ (préréglage − tendanciel), élément par élément.
    const merged = new Map<string, GapItem>();
    for (const i of itemsTrend) merged.set(`${i.kind}:${i.id}`, { ...i });
    for (const t of theirs) {
      const key = `${t.kind}:${t.id}`;
      const dv = t.indicators?.[d.id] ?? 0;
      const prev = merged.get(key);
      if (prev) prev.delta -= dv;
      else merged.set(`reference:${t.id}`, { kind: "reference", id: t.id, label: t.label, delta: -dv });
    }
    const itemsPlf = order([...merged.values()]);
    const sum = (xs: GapItem[]): number => xs.reduce((a, x) => a + x.delta, 0);
    const gapT = player !== null && trend !== null ? player - trend : null;
    const gapP = player !== null && plf !== null ? player - plf : null;
    const resT = gapT !== null ? gapT - sum(itemsTrend) : null;
    const resP = gapP !== null ? gapP - sum(itemsPlf) : null;
    return {
      id: d.id,
      vs_trend: { gap: gapT, items: itemsTrend, residual: resT, rationale: gapRationale(d, r.year, player, trend, "si rien ne change", itemsTrend, resT) },
      vs_plf: { gap: gapP, items: itemsPlf, residual: resP, rationale: gapRationale(d, r.year, player, plf, `avec ${r.preset.label.charAt(0).toLowerCase()}${r.preset.label.slice(1)}`, itemsPlf, resP) },
    };
  });
  return { year: r.year, indicators };
}
